import { useCallback, useEffect, useMemo, useState } from 'react';
import { loadMyReferral, logReferralShare } from '../api/referralsApi';

const EMPTY_STATS = {
  shares_total: 0,
  clicks_total: 0,
  registered_total: 0,
  qualified_total: 0,
  rewarded_total: 0,
  earned_xp_total: 0,
};

function normalizeReferralData(data) {
  const source = data && typeof data === 'object' ? data : {};
  const stats = source.stats && typeof source.stats === 'object' ? source.stats : {};

  return {
    code: String(source.code || '').trim(),
    link: String(source.link || '').trim(),
    shareText: String(source.share_text || source.shareText || '').trim(),
    settings: source.settings && typeof source.settings === 'object' ? source.settings : {},
    stats: {
      ...EMPTY_STATS,
      ...stats,
    },
  };
}

export function useReferrals() {
  const [referralData, setReferralData] = useState(() => normalizeReferralData(null));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await loadMyReferral();
      const normalized = normalizeReferralData(data);
      setReferralData(normalized);
      return normalized;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload().catch(() => {});
  }, [reload]);

  const trackShare = useCallback(async (channel) => {
    const safeChannel = String(channel || '').trim();

    if (!safeChannel) {
      return referralData.stats;
    }

    try {
      const result = await logReferralShare(safeChannel);
      setReferralData((prev) => ({
        ...prev,
        stats: {
          ...prev.stats,
          shares_total: Number(result?.shares_total ?? prev.stats.shares_total ?? 0),
        },
      }));

      return result;
    } catch (err) {
      console.warn('Referral share tracking failed', err);
      return null;
    }
  }, [referralData.stats]);

  const stats = useMemo(() => ({ ...EMPTY_STATS, ...(referralData.stats || {}) }), [referralData.stats]);

  return {
    referralData,
    stats,
    loading,
    error,
    reload,
    trackShare,
  };
}

export default useReferrals;
